import {ButtonGroup, TextField, useMediaQuery} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import 'Agent/AgentForms/Marketing/components//MarketingFormQuantity.sass';
import 'Agent/AgentForms/Marketing/components/Marketing.sass';
import {
  changeCartVisibility,
  changeCheckoutCart,
  CheckoutCart,
  DEFAULT_FORM_NUMBER,
  MarketingViewCart,
  validateOrderAmount,
  resetOrderAmount,
  currentQuantity,
  cartValue,
} from 'Agent/AgentForms/Marketing/ducks/Marketing';
import {Marketing} from 'Agent/AgentForms/Marketing/models/Marketing';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {ShoppingCartQuantities} from 'Shared/constants/ShoppingCart';

export type MarketingFormQuantityProps = {
  marketing: Marketing;
  cartData: MarketingViewCart;
  checkoutItems: CheckoutCart;
};

const MarketingFormQuantity = ({marketing, cartData, checkoutItems}: MarketingFormQuantityProps) => {
  const [viewCart, setViewCart] = useState(cartData);
  const [checkoutCart, setCheckoutCart] = useState(checkoutItems);
  const [inQuantityField, setInQuantityField] = useState(false);
  const dispatch = useDispatch();
  const {inCartVisible} = useSelector((state: ReducerState) => state.marketing);

  const currentFormNumber = marketing.formNumber ? marketing.formNumber : DEFAULT_FORM_NUMBER;
  const currentFormMax = marketing.maxQuantity ? marketing.maxQuantity : ShoppingCartQuantities.Max;
  const currentFormMin = marketing.minQuantity ? marketing.minQuantity : ShoppingCartQuantities.Min;
  const currentFormIncrement = marketing.unitIncrements ? marketing.unitIncrements : ShoppingCartQuantities.Increment;

  const [data, setData] = useState<any>(cartValue(checkoutCart, currentFormNumber, viewCart));

  const onQuantityChange = (value: any) => {
    if (!isNaN(value) && value != '' && Number(value) <= 0) {
      if (value < currentFormMin) {
        value = currentFormMin;
      }
      setData(value);
      let data: Marketing | undefined = viewCart.items.get(currentFormNumber);
      if (data !== undefined) {
        viewCart.items.delete(currentFormNumber);
        setViewCart({...viewCart});
        const index = inCartVisible.inCartFormNumber.indexOf(currentFormNumber);
        delete inCartVisible.inCartFormNumber[index];
        dispatch(changeCartVisibility({...inCartVisible}));
      } else {
        checkoutCart.items.delete(currentFormNumber);
        dispatch(changeCheckoutCart({...checkoutCart}));
      }
    } else {
      if (!isNaN(value) && Number(value) <= 9999) {
        setData(value);
        let data: Marketing | undefined = viewCart.items.get(currentFormNumber);
        if (data !== undefined) {
          viewCart.items.set(currentFormNumber, {...marketing, quantity: value});
        } else {
          checkoutCart.items.set(currentFormNumber, value);
          setCheckoutCart({...checkoutCart});
        }
        setViewCart({...viewCart});
      }
    }
  };

  const addOne = () => {
    let data: Marketing | undefined = viewCart.items.get(currentFormNumber);
    let num = data ? Number(data.quantity) + Number(currentFormIncrement) : 0;
    if (num < currentFormMin) {
      num = currentFormMin;
    }
    if (num <= currentFormMax) {
      if (data === undefined) {
        let currentCheckoutItem = checkoutCart.items.get(currentFormNumber);
        let quantity =
          currentCheckoutItem === undefined
            ? currentFormMin
            : Number(currentCheckoutItem) < currentFormMax
            ? Number(currentCheckoutItem) + currentFormIncrement
            : Number(currentCheckoutItem);
        if (quantity > 0 && quantity < currentFormMin) quantity = currentFormMin;
        checkoutCart.items.set(currentFormNumber, quantity);
        setCheckoutCart({...checkoutCart});
      }
      if (data !== undefined) {
        viewCart.items.set(currentFormNumber, {...marketing, quantity: Number(num)});
      }
      setViewCart({...viewCart});
    }
  };

  const subtractOne = () => {
    let data: Marketing | undefined = viewCart.items.get(currentFormNumber);
    if (data === undefined) {
      let currentCheckoutItem = checkoutCart.items.get(currentFormNumber);
      let quantity =
        currentCheckoutItem === undefined || currentCheckoutItem === 0
          ? 0
          : Number(currentCheckoutItem) - currentFormIncrement;
      if (quantity < currentFormMin) quantity = 0;
      checkoutCart.items.set(currentFormNumber, quantity);
      setCheckoutCart({...checkoutCart});
    } else {
      let num = data ? Number(data.quantity) - Number(currentFormIncrement) : 0;
      if (num < currentFormMin) {
        num = 0;
      }
      if (Number(num) > 0) {
        viewCart.items.set(currentFormNumber, {...marketing, quantity: Number(num)});
        setViewCart({...viewCart});
      } else {
        removeItemFromCart();
      }
    }
  };

  const removeItemFromCart = () => {
    viewCart.items.delete(currentFormNumber);
    setViewCart({...viewCart});
    const index = inCartVisible.inCartFormNumber.indexOf(currentFormNumber);
    delete inCartVisible.inCartFormNumber[index];
    dispatch(changeCartVisibility({...inCartVisible}));
  };

  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

  const handleBlanks = (value: any) => {
    let quantity = validateOrderAmount(value, currentFormMin, currentFormMax, currentFormIncrement);
    if (value == '' || value == 0) {
      quantity = 0;
    }

    if (quantity != value) {
      resetOrderAmount(quantity, currentFormNumber, marketing, checkoutCart, setCheckoutCart, viewCart, setViewCart);
    }
    if (
      viewCart.items.get(currentFormNumber) !== undefined &&
      String(viewCart.items.get(currentFormNumber)!.quantity) === ''
    ) {
      viewCart.items.get(currentFormNumber)!.quantity = Number(currentFormMin);
      setViewCart({...viewCart});
    }
    if (value == '') {
      removeItemFromCart();
    }
    setInQuantityField(false);
  };
  const handleQuantityFocus = (event: any) => {
    event.target.select();
    setInQuantityField(true);
  };
  return (
    <div className="marketing_quantity">
      {
        <ButtonGroup size="small" aria-label="small outlined button group" style={{marginLeft: '-1rem'}}>
          <Button
            disabled={
              !(marketing && marketing.formNumber) ||
              currentQuantity(checkoutCart, currentFormNumber, viewCart) == 0 ||
              inQuantityField
            }
            variant="text"
            id="subtract-one-btn"
            onClick={e => {
              return subtractOne();
            }}
          >
            -
          </Button>
          <TextField
            disabled={!(marketing && marketing.formNumber)}
            fullWidth={isMobile}
            onFocus={event => {
              handleQuantityFocus(event);
            }}
            id="standard-basic"
            value={cartValue(checkoutCart, currentFormNumber, viewCart)}
            helperText={isNaN(data) ? 'Only numbers allowed' : ''}
            error={isNaN(data)}
            onChange={event => {
              onQuantityChange(event.target.value);
            }}
            onBlur={event => {
              handleBlanks(event.target.value);
            }}
          />
          <Button
            variant="text"
            id="add-one-btn"
            disabled={
              !(marketing && marketing.formNumber) ||
              currentQuantity(checkoutCart, currentFormNumber, viewCart) >= currentFormMax ||
              inQuantityField
            }
            onClick={e => {
              return addOne();
            }}
          >
            +
          </Button>
        </ButtonGroup>
      }
    </div>
  );
};
export default MarketingFormQuantity;
